import styled, { css } from "styled-components";
import { ChangeEvent, FC, useState, DragEvent } from "react";

import { ReactComponent as ImgIcon } from "@/assets/icons/img-placeholder.svg";
import useAlerts from "@/hooks/useAlerts";
import { InnerLabel, Notice, Title } from "@/components/Forms/FileField";
import { SrOnly } from "@/components/Layout/Parts";

const Dropzone = styled.div<{ $dragging: boolean }>`
  border-radius: 4px;
  border: 1px dashed ${({ theme }) => theme.colors.lightGray};
  color: ${({ theme }) => theme.colors.gray};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 42px;
  ${({ theme, $dragging }) =>
    $dragging &&
    css`
      border-color: ${theme.colors.purpleLight};
    `}
`;

const Box = styled.div`
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  margin-bottom: 24px;
  padding: 24px;
`;

interface Props {
  setUploadedImages: (value: File[]) => void;
}

const UploadFile: FC<Props> = ({ setUploadedImages }) => {
  const [dragging, setDragging] = useState(false);

  const alerts = useAlerts();

  const validateFilesType = (files: FileList) => {
    for (let i = 0; i < files.length; i++) {
      if (!["image/png"].includes(files[i].type)) {
        return alerts.error(
          "Invalid file type!",
          "Please use image of type png"
        );
      }
    }
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;

    if (files?.length) {
      const newArr = [];

      for (let i = 0; i < files.length; i++) {
        newArr.push(files[i]);
      }

      setUploadedImages(newArr);
    }
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer?.items?.length) {
      setDragging(true);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    if (e.dataTransfer?.files?.length) {
      const droppedFiles = e.dataTransfer.files;

      if (validateFilesType(droppedFiles)) return;

      if (!!droppedFiles?.length) {
        const newArr = [];

        for (let i = 0; i < droppedFiles.length; i++) {
          newArr.push(droppedFiles[i]);
        }

        setUploadedImages(newArr);
      }
    }
  };
  return (
    <Box>
      <Dropzone
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        $dragging={dragging}
      >
        <ImgIcon />
        <Title $type="smallBold" $color="gray" as="div">
          Drop images here or{" "}
          <InnerLabel
            as="label"
            $type="smallBold"
            $color="purple"
            htmlFor="images-input"
          >
            browse
            <SrOnly
              as="input"
              type="file"
              id="images-input"
              accept="image/png"
              multiple
              onChange={onChange}
            />
          </InnerLabel>
        </Title>
        <Notice $type="extraSmall" $color="gray" as="div">
          Supports: transparent PNG, min 960x1080
        </Notice>
      </Dropzone>
    </Box>
  );
};

export default UploadFile;
